// Copyright (c) 2026 Solartek LLC. All rights reserved.
import { z } from 'zod';
import { IMPORT_STATUSES } from '../enums.js';
import { isoDateTimeSchema, jsonObjectSchema, paginationQuerySchema, uuidSchema } from '../common.js';
import { IMPORT_KINDS, IMPORT_SOURCES } from '../hrms.js';
export const IMPORT_ROW_STATUSES = ['valid', 'invalid', 'imported', 'skipped', 'failed'];
/** JSON upload body (alternative to multipart/form-data with a `file` field). */
export const importUploadSchema = z.object({
    fileName: z.string().trim().min(1).max(200),
    /** Base64-encoded file content (CSV, or an Excel .xlsx workbook — its first sheet is read). */
    contentBase64: z.string().min(1).max(20 * 1024 * 1024),
    /** What the file holds: employees (default) or organisational units (departments). */
    kind: z.enum(IMPORT_KINDS).default('EMPLOYEES'),
    options: z.object({
        /** Update existing employees matched by employee number instead of reporting them as duplicates. */
        updateExisting: z.boolean().default(false),
        autoAssignDeviceUserId: z.boolean().default(true),
    }).partial().default({}),
});
export const importRowErrorSchema = z.object({ field: z.string().nullable(), message: z.string() });
export const importJobRowDtoSchema = z.object({
    id: z.string(),
    rowNo: z.number().int(),
    data: jsonObjectSchema,
    errors: z.array(importRowErrorSchema),
    status: z.enum(IMPORT_ROW_STATUSES),
    entityId: uuidSchema.nullable(),
});
export const importJobDtoSchema = z.object({
    id: uuidSchema,
    organizationId: uuidSchema,
    type: z.string(),
    /** `upload` (the import page) or `hrms` (a batch an HR system sent with an API key). */
    source: z.enum(IMPORT_SOURCES).optional(),
    originalFilename: z.string().nullable(),
    status: z.enum(IMPORT_STATUSES),
    totalRows: z.number().int(),
    validRows: z.number().int(),
    errorRows: z.number().int(),
    importedRows: z.number().int(),
    options: jsonObjectSchema,
    summary: jsonObjectSchema.nullable(),
    error: z.string().nullable(),
    queueJobId: z.string().nullable(),
    requestedBy: uuidSchema.nullable(),
    requestedByName: z.string().nullable().optional(),
    confirmedBy: uuidSchema.nullable(),
    confirmedAt: isoDateTimeSchema.nullable(),
    createdAt: isoDateTimeSchema,
    updatedAt: isoDateTimeSchema,
    /** First rows (max 50) returned on upload and on detail. */
    preview: z.array(importJobRowDtoSchema).optional(),
});
export const importJobListQuerySchema = paginationQuerySchema.extend({ status: z.enum(IMPORT_STATUSES).optional(), kind: z.enum(IMPORT_KINDS).optional() });
export const importJobRowsQuerySchema = paginationQuerySchema.extend({ status: z.enum(IMPORT_ROW_STATUSES).optional() });
//# sourceMappingURL=import-job.js.map